import React from 'react';
import { Table, Empty, Spin, Tag, Card, Tooltip } from 'antd';
import dayjs from 'dayjs';

const TransfersTable = ({ 
  transfers = [], 
  loading = false, 
  title = "Associated Transfers",
  showCard = true 
}) => {
  const columns = [
    {
      title: 'Transfer ID',
      dataIndex: 'TransferID',
      key: 'TransferID',
      width: 120,
      render: (id) => `#${id}`,
    },
    {
      title: 'Points',
      dataIndex: 'Points',
      key: 'Points',
      width: 100,
      render: (points) => (
        <Tag color={points >= 0 ? 'green' : 'red'}>
          {points >= 0 ? '+' : ''}{points}
        </Tag>
      ),
      sorter: (a, b) => a.Points - b.Points,
    },
    {
      title: 'From Agent',
      key: 'FromAgent',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.FromAgentName || 'N/A'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.FromAgentEmail}</div>
        </div>
      ),
    },
    {
      title: 'To Agent',
      key: 'ToAgent',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.ToAgentName || 'N/A'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.ToAgentEmail}</div>
        </div>
      ),
    },
    {
      title: 'Transfer Date',
      dataIndex: 'TransferDate',
      key: 'TransferDate',
      width: 140,
      render: (date) => dayjs(date).format('MMM DD, YYYY HH:mm'),
      sorter: (a, b) => new Date(a.TransferDate).getTime() - new Date(b.TransferDate).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Type',
      key: 'transferType',
      width: 140,
      render: (_, record) => {
        const status = record.TransactionStatus;
        const hasSubmission = record.SubmissionTransactionID; // Updated field name
        const isReversed = record.ReversedFlag === "R";
        
        // Determine transfer type based on status and submission link
        if (status === "SHAR") {
          return (
            <Tag color={isReversed ? "orange" : "blue"} style={{ fontSize: '11px' }}>
              🔗 Submission Share{isReversed ? " (Reversed)" : ""}
            </Tag>
          );
        }
        if (status === "MANU") {
          return (
            <Tag color={isReversed ? "orange" : "cyan"} style={{ fontSize: '11px' }}>
              ✋ Manual Transfer{isReversed ? " (Reversed)" : ""}
            </Tag>
          );
        }
        if (status === "RVSN") {
          return (
            <Tag color="purple" style={{ fontSize: '11px' }}>
              ↩️ Reversal Transaction
            </Tag>
          );
        }
        if (hasSubmission && !status.includes("RVSN")) {
          return (
            <Tag color={isReversed ? "orange" : "green"} style={{ fontSize: '11px' }}>
              🤖 Rule-Based{isReversed ? " (Reversed)" : ""}
            </Tag>
          );
        }
        return (
          <Tag color={isReversed ? "orange" : "default"} style={{ fontSize: '11px' }}>
            📋 Transfer{isReversed ? " (Reversed)" : ""}
          </Tag>
        );
      },
    },
    {
      title: 'Notes',
      dataIndex: 'Notes',
      key: 'Notes',
      ellipsis: true,
      render: (notes, record) => {
        const noteText = notes || 'No notes';
        const isLongNote = noteText.length > 50; // Show tooltip if note is longer than 50 characters
        const isSubmissionShare = record.TransactionStatus === "SHAR";
        const submissionId = record.SubmissionTransactionID;
        
        // For submission sharing, highlight the submission ID
        let displayText = noteText;
        if (isSubmissionShare && submissionId) {
          // Extract submission ID from notes and make it stand out
          displayText = noteText.replace(
            new RegExp(`submission ${submissionId}`, 'gi'),
            `📄 submission ${submissionId}`
          );
        }
        
        const noteElement = (
          <span style={{ 
            color: notes?.includes('REVERSED') ? '#ff4d4f' : 
                   isSubmissionShare ? '#1890ff' : 'inherit',
            fontWeight: notes?.includes('REVERSED') ? 500 : 
                        isSubmissionShare ? 500 : 'normal'
          }}>
            {displayText}
          </span>
        );

        // Enhanced tooltip for submission shares
        const tooltipTitle = isSubmissionShare && submissionId ? 
          `${noteText}\n\n💡 This is a submission sharing transfer for submission #${submissionId}` : 
          noteText;

        // Only wrap with Tooltip if the note is long enough to be truncated
        return isLongNote ? (
          <Tooltip 
            title={tooltipTitle} 
            placement="topLeft"
            overlayStyle={{ maxWidth: '400px' }}
          >
            {noteElement}
          </Tooltip>
        ) : noteElement;
      },
    },
    {
      title: 'Transferred By',
      key: 'TransferredBy',
      width: 180,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.TransferredByAdminName || 'N/A'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.TransferredByAdminEmail}</div>
        </div>
      ),
    },
  ];

  const tableContent = (
    <>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading transfers...</div>
        </div>
      ) : transfers.length === 0 ? (
        <Empty 
          description="No transfers found for this rule"
          style={{ padding: '40px' }}
        />
      ) : (
        <Table
          dataSource={transfers}
          rowKey="TransferID"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} transfers`,
          }}
          scroll={{ x: 800 }}
          columns={columns}
        />
      )}
    </>
  );

  if (!showCard) {
    return tableContent;
  }

  return (
    <Card 
      title={title}
      extra={
        <Tag color={transfers.length > 0 ? "blue" : "default"}>
          {transfers.length} Transfer{transfers.length !== 1 ? 's' : ''}
        </Tag>
      }
    >
      {tableContent}
    </Card>
  );
};

export default TransfersTable;






